import { useEffect, useMemo, useState } from 'react';
import { Dropdown } from 'sharedUi/Dropdown';
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';

// -----------------------------------------------------------------------------
// PhoneField — country-aware phone input.
//
// The number of digits allowed varies by the selected country: libphonenumber's
// rules cap the national number as you type (extra keystrokes past the country's
// max length are ignored) and format it live. The value emitted upward is always
// E.164 (e.g. "+919876543210"), so a plain isValidPhoneNumber(value) in the form
// schema is enough to validate the country-correct digit count — no country hint
// needs to be threaded through.
// -----------------------------------------------------------------------------

interface CountryOption {
  value: CountryCode;
  code: string; // calling code, no "+"
  name: string;
  flag: string;
}

// ISO-3166 alpha-2 → 🇺🇸 regional-indicator emoji.
const toFlag = (iso: string) =>
  iso.replace(/./g, (c) => String.fromCodePoint(127397 + (c.codePointAt(0) ?? 0)));

const regionNames =
  typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

const COUNTRY_OPTIONS: CountryOption[] = getCountries()
  .map((iso) => ({
    value: iso,
    code: getCountryCallingCode(iso),
    name: regionNames?.of(iso) || iso,
    flag: toFlag(iso),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

const DEFAULT_COUNTRY: CountryCode = 'IN';

// Max national digits allowed per country, derived from that country's example
// mobile number (India → 10, UAE → 9, …). Used as a hard cap on live input so the
// digit count varies by country; it's a MAX, so shorter valid numbers still pass.
// Cached lazily; falls back to 15 (E.164 global max) for unknown countries.
const maxLenCache = new Map<CountryCode, number>();
const maxLenFor = (country: CountryCode): number => {
  const cached = maxLenCache.get(country);
  if (cached !== undefined) return cached;
  const ex = getExampleNumber(country, examples);
  const len = ex ? ex.nationalNumber.length : 15;
  maxLenCache.set(country, len);
  return len;
};

interface PhoneFieldProps {
  value: string | undefined;
  onChange: (e164: string) => void;
  onBlur?: () => void;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  name?: string;
}

const PhoneField = ({
  value,
  onChange,
  onBlur,
  label = 'Phone',
  required = false,
  error = false,
  helperText,
  disabled = false,
  name = 'phone',
}: PhoneFieldProps) => {
  const [country, setCountry] = useState<CountryCode>(DEFAULT_COUNTRY);
  const [national, setNational] = useState('');
  const [focused, setFocused] = useState(false);
  // Mirror PrimeReact's FloatLabel: the label rests inside the field and lifts to
  // the border only once focused or filled, so this field lines up with the plain
  // FloatLabel inputs beside it (e.g. Email) in every state.
  const floated = focused || national.length > 0;

  // Sync FROM the external value (edit prefill, form reset). Parse E.164 to
  // recover the country + national digits; a bare number is treated as national
  // digits under the current country.
  useEffect(() => {
    const incoming = (value || '').trim();
    // Rebuild what we'd currently emit; skip if it already matches to avoid loops.
    const current = national ? `+${getCountryCallingCode(country)}${national}` : '';
    if (incoming === current) return;
    if (!incoming) {
      setNational('');
      return;
    }
    const parsed = parsePhoneNumberFromString(incoming);
    if (parsed?.country) {
      setCountry(parsed.country);
      setNational(parsed.nationalNumber);
    } else {
      setNational(incoming.replace(/[^\d]/g, ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = (digits: string, c: CountryCode) => {
    onChange(digits ? `+${getCountryCallingCode(c)}${digits}` : '');
  };

  const handleDigits = (raw: string) => {
    // Strip non-digits and cap at the country's max length (letters can never be
    // entered; a pasted over-long value is truncated rather than rejected).
    const digits = raw.replace(/[^\d]/g, '').slice(0, maxLenFor(country));
    setNational(digits);
    emit(digits, country);
  };

  const handleCountry = (c: CountryCode) => {
    setCountry(c);
    // Re-cap the existing digits against the new country, then re-emit.
    const digits = national.slice(0, maxLenFor(c));
    setNational(digits);
    emit(digits, c);
  };

  const display = useMemo(() => {
    if (!national) return '';
    const ayt = new AsYouType(country);
    return ayt.input(national);
  }, [national, country]);

  const selected = COUNTRY_OPTIONS.find((o) => o.value === country);

  return (
    <div className="w-full">
      <div
        // mt-3 matches the .p-float-label wrapper's 0.75rem top margin so this
        // field's box lines up with the FloatLabel inputs beside it (e.g. Email).
        className={`mt-3 flex h-10 items-stretch rounded-md border bg-white transition-colors dark:bg-slate-900 focus-within:ring-2 ${
          error
            ? 'border-red-500 focus-within:border-red-500 focus-within:ring-red-500/20'
            : 'border-slate-300 hover:border-slate-400 focus-within:border-indigo-500 focus-within:ring-indigo-500/20 dark:border-slate-600'
        }`}
      >
        <Dropdown
          value={country}
          options={COUNTRY_OPTIONS}
          optionValue="value"
          onChange={(e) => handleCountry(e.value as CountryCode)}
          disabled={disabled}
          filter
          filterBy="name,code"
          resetFilterOnHide
          appendTo={document.body}
          // Virtualise the ~250-country list so only the visible rows render —
          // otherwise every open re-renders all rows and stutters.
          virtualScrollerOptions={{ itemSize: 38 }}
          className="phone-country-dd shrink-0"
          panelClassName="phone-country-panel"
          valueTemplate={() =>
            selected ? (
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <span className="text-base leading-none">{selected.flag}</span>
                <span className="text-sm font-medium text-slate-700 dark:text-slate-200">+{selected.code}</span>
              </span>
            ) : null
          }
          itemTemplate={(o: CountryOption) => (
            <span className="flex items-center gap-2 whitespace-nowrap">
              <span className="text-base leading-none">{o.flag}</span>
              <span className="flex-1">{o.name}</span>
              <span className="text-slate-400">+{o.code}</span>
            </span>
          )}
        />
        {/* Number input + floating label, mirroring FloatLabel behaviour. The
            label rests inside (over the input) when empty and lifts to the top
            border on focus/fill — so it tracks the Email field beside it. */}
        <div className="relative min-w-0 flex-1 border-l border-line">
          <input
            id={name}
            name={name}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            disabled={disabled}
            value={display}
            onChange={(e) => handleDigits(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              setFocused(false);
              onBlur?.();
            }}
            placeholder={floated ? getExampleNumber(country, examples)?.formatNational() || '' : ''}
            className="h-full w-full bg-transparent px-3 py-2.5 text-sm tracking-wide outline-none focus:ring-0 disabled:opacity-60"
          />
          <label
            htmlFor={name}
            className={`pointer-events-none absolute left-3 bg-white px-1 text-slate-500 transition-all dark:bg-slate-900 dark:text-slate-400 ${
              floated
                ? '-top-2 text-xs'
                : 'top-1/2 -translate-y-1/2 bg-transparent px-0 text-sm text-slate-400 dark:bg-transparent'
            }`}
          >
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>
        </div>
      </div>
      {helperText && (
        <small className={error ? 'p-error' : 'text-gray-500'}>{helperText}</small>
      )}
    </div>
  );
};

export default PhoneField;
